import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const SELLING_PLAN_CONFIG = {
  // Special Order
  '691469713757': {
    mode: 'special_order',
    buttonText: window.sellingPlanLabels?.specialOrder?.buttonText || 'Special Order now',
    badgeText: window.sellingPlanLabels?.specialOrder?.badgeText || 'Special Order',
    purchaseType: window.sellingPlanLabels?.specialOrder?.purchaseType || 'Special order',
    availabilityDate: window.sellingPlanLabels?.specialOrder?.availabilityDate || '2/4 weeks',
    payment: window.sellingPlanLabels?.specialOrder?.payment || 'Full payment at checkout',
  },

  // Preorder
  '691429638493': {
    mode: 'preorder',
    buttonText: window.sellingPlanLabels?.preorder?.buttonText || 'Pre-order now',
    badgeText: window.sellingPlanLabels?.preorder?.badgeText || 'Pre-order',
    purchaseType: window.sellingPlanLabels?.preorder?.purchaseType || 'Preorder',
    availabilityDate: window.sellingPlanLabels?.preorder?.availabilityDate || '6 months',
    payment: window.sellingPlanLabels?.preorder?.payment || 'Partial payment at checkout',
  },
};

const MODE_CONFIG = {
  special_order: {
    sellingPlanId: '691469713757',
    buttonText:
      window.sellingPlanLabels?.specialOrder?.buttonText ||
      window.themeStrings?.specialOrderNow ||
      'Special Order now',
    message:
      window.themeStrings?.globalSpecialOrderText ||
      window.sellingPlanLabels?.specialOrder?.message ||
      '',
  },

  preorder: {
    sellingPlanId: '691429638493',
    buttonText:
      window.sellingPlanLabels?.preorder?.buttonText ||
      window.themeStrings?.preorderNow ||
      'Pre-order now',
    message:
      window.themeStrings?.globalPreorderText ||
      window.sellingPlanLabels?.preorder?.message ||
      '',
  },

  notify: {
    sellingPlanId: '',
    buttonText:
      window.themeStrings?.notifyMe ||
      window.themeStrings?.notifyMeText ||
      'Notify me',
    message:
      window.themeStrings?.globalNotifyMeText ||
      window.themeStrings?.notifyMeMessage ||
      '',
  },

  normal: {
    sellingPlanId: '',
    buttonText:
      window.themeStrings?.addToCart ||
      'Add to cart',
    message: '',
  },
  select_size: {
    sellingPlanId: '',
    buttonText:
      window.themeStrings?.selectSize ||
      (
        (document.documentElement.lang || '').toLowerCase().startsWith('it')
          ? 'SELEZIONA TAGLIA'
          : 'SELECT SIZE'
      ),
    message: '',
  },
};

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #pendingContent = new Map();
  #activeRequestId = 0;
  #cartUpdateAbortController = new AbortController();
  #variantNoteObserver = null;
  #variantNoteAbortController = null;
  #viewportPrefetchObserver = null;

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const hotspotProduct = this.closest('product-hotspot-component');
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    const selectedVariantId = this.#getSelectedVariantId();

    if (!url.searchParams.has('variant') && selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() || null;
  }

  #handleVariantSelected = (event) => {
    this.#updateQuickAddButtonState(event);
  };

  #prefetchQuickAdd = () => {
    const currentUrl = this.productPageUrl;

    if (
      !currentUrl ||
      this.#cachedContent.has(currentUrl) ||
      this.#pendingContent.has(currentUrl)
    ) {
      return Promise.resolve(null);
    }

    return this.#loadProductGrid(currentUrl).catch((error) => {
      console.warn('[Quick Add] Product prefetch failed:', error);
      return null;
    });
  };

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });

    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    /*
     * Start loading before the click whenever possible.
     * This makes the Quick Add content appear much faster on desktop,
     * keyboard navigation and touch devices.
     */
    this.addEventListener('pointerenter', this.#prefetchQuickAdd, { passive: true });
    this.addEventListener('focusin', this.#prefetchQuickAdd);
    this.addEventListener('touchstart', this.#prefetchQuickAdd, { passive: true });

    /*
     * Prefetch Quick Add content shortly before the product card reaches the
     * viewport. This is especially useful on touch devices, where there is no
     * hover time before the user taps the plus button.
     */
    if ('IntersectionObserver' in window) {
      this.#viewportPrefetchObserver = new IntersectionObserver(
        (entries) => {
          const isNearViewport = entries.some((entry) => entry.isIntersecting);
          if (!isNearViewport) return;

          this.#viewportPrefetchObserver?.disconnect();
          this.#viewportPrefetchObserver = null;

          const startPrefetch = () => {
            if (!this.isConnected) return;
            void this.#prefetchQuickAdd();
          };

          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(startPrefetch, { timeout: 700 });
          } else {
            window.setTimeout(startPrefetch, 120);
          }
        },
        {
          root: null,
          rootMargin: '350px 0px',
          threshold: 0.01,
        }
      );

      this.#viewportPrefetchObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    document.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    this.removeEventListener('pointerenter', this.#prefetchQuickAdd);
    this.removeEventListener('focusin', this.#prefetchQuickAdd);
    this.removeEventListener('touchstart', this.#prefetchQuickAdd);
    this.#viewportPrefetchObserver?.disconnect();
    this.#viewportPrefetchObserver = null;

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#variantNoteObserver?.disconnect();
    this.#variantNoteAbortController?.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  };

  async #loadProductGrid(productPageUrl) {
    if (!productPageUrl) return null;

    const cachedContent = this.#cachedContent.get(productPageUrl);

    if (cachedContent) {
      return cachedContent;
    }

    const pendingRequest = this.#pendingContent.get(productPageUrl);

    if (pendingRequest) {
      return pendingRequest;
    }

    const request = (async () => {
      const html = await this.fetchProductPage(productPageUrl);

      if (!html) return null;

      const gridElement = html.querySelector('[data-product-grid-content]');

      if (!gridElement) {
        console.warn(
          '[Quick Add] Could not find [data-product-grid-content] on:',
          productPageUrl
        );

        return null;
      }

      const productGrid = gridElement.cloneNode(true);
      this.#cachedContent.set(productPageUrl, productGrid);

      return productGrid;
    })();

    this.#pendingContent.set(productPageUrl, request);

    try {
      return await request;
    } finally {
      if (this.#pendingContent.get(productPageUrl) === request) {
        this.#pendingContent.delete(productPageUrl);
      }
    }
  }

  #showQuickAddLoading() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.replaceChildren();

    const loading = document.createElement('div');
    loading.classList.add(
      'quick-add-custom-layout',
      'quick-add-custom-layout--loading'
    );
    loading.setAttribute('data-quick-add-loading', '');
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-live', 'polite');

    const spinner = document.createElement('span');
    spinner.classList.add('quick-add-loading__spinner');
    spinner.setAttribute('aria-hidden', 'true');

    const loadingText = document.createElement('span');
    loadingText.classList.add('quick-add-loading__text');
    loadingText.textContent =
      window.themeStrings?.loading ||
      (
        (document.documentElement.lang || '').toLowerCase().startsWith('it')
          ? 'Caricamento...'
          : 'Loading...'
      );

    loading.append(spinner, loadingText);
    modalContent.appendChild(loading);
  }

  #showQuickAddError() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    modalContent.removeAttribute('aria-busy');
    modalContent.replaceChildren();

    const errorMessage = document.createElement('div');
    errorMessage.classList.add(
      'quick-add-custom-layout',
      'quick-add-custom-layout--error'
    );
    errorMessage.setAttribute('role', 'alert');
    errorMessage.textContent =
      window.themeStrings?.quickAddError ||
      (
        (document.documentElement.lang || '').toLowerCase().startsWith('it')
          ? 'Impossibile caricare il prodotto. Riprova.'
          : 'Unable to load this product. Please try again.'
      );

    modalContent.appendChild(errorMessage);
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const requestId = ++this.#activeRequestId;
    const cachedProductGrid = this.#cachedContent.get(currentUrl);

    /*
     * Cached content is already available, so render it before opening
     * without showing the loading state.
     */
    if (cachedProductGrid) {
      await this.updateQuickAddModal(cachedProductGrid.cloneNode(true));

      if (requestId !== this.#activeRequestId) return;

      this.#openQuickAddModal();
      return;
    }

    /*
     * Open the drawer immediately instead of waiting for the full product
     * page request and DOM processing to finish.
     */
    this.#showQuickAddLoading();
    this.#openQuickAddModal();

    try {
      const productGrid = await this.#loadProductGrid(currentUrl);

      /*
       * Ignore an older response if another Quick Add request was started.
       */
      if (requestId !== this.#activeRequestId) return;

      if (!productGrid) {
        this.#showQuickAddError();
        return;
      }

      await this.updateQuickAddModal(productGrid.cloneNode(true));
    } catch (error) {
      if (requestId !== this.#activeRequestId) return;

      console.error('[Quick Add] Unable to load product:', error);
      this.#showQuickAddError();
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const modalContent = dialogComponent.querySelector('#quick-add-modal-content');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;

    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    const requestController = new AbortController();
    this.#abortController = requestController;

    try {
      const response = await fetch(productPageUrl, {
        signal: requestController.signal,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      if (this.#abortController === requestController) {
        this.#abortController = null;
      }
    }
  }

  #getVariantNotesJson(productGrid) {
    const dataFromQuickAdd = this.getAttribute('data-variant-size-notes-json');

    if (dataFromQuickAdd) {
      return dataFromQuickAdd;
    }

    const script = productGrid.querySelector('[data-variant-size-notes]');
    return script?.textContent?.trim() || '{}';
  }

  #buildQuickAddContent(productGrid) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('quick-add-custom-layout');
    wrapper.setAttribute('data-product-grid-content', '');
    wrapper.setAttribute('data-variant-size-notes-json', this.#getVariantNotesJson(productGrid));

    const title = productGrid.querySelector('.view-product-title')?.cloneNode(true);
    const price = productGrid.querySelector('product-price')?.cloneNode(true);
    const media = productGrid.querySelector('.product-information__media')?.cloneNode(true);
    const variantPicker = productGrid.querySelector('variant-picker')?.cloneNode(true);
    const buyButtonsBlock = productGrid.querySelector('.buy-buttons-block')?.cloneNode(true);
    const productFormComponent = productGrid.querySelector('product-form-component')?.cloneNode(true);
    const viewMoreDetails = productGrid.querySelector('.view-more-details__wrapper')?.cloneNode(true);

    const headerWrapper = document.createElement('div');
    headerWrapper.classList.add('quick-add-custom-layout__header');

    if (title) {
      headerWrapper.appendChild(title);
    } else {
      const fallbackTitle = document.createElement('div');
      fallbackTitle.classList.add('view-product-title');

      const fallbackTitleLink = document.createElement('a');
      fallbackTitleLink.href = this.productPageUrl;
      fallbackTitleLink.textContent = this.dataset.productTitle || '';

      fallbackTitle.appendChild(fallbackTitleLink);
      headerWrapper.appendChild(fallbackTitle);
    }

    if (price) headerWrapper.appendChild(price);

    const mediaWrapper = document.createElement('div');
    mediaWrapper.classList.add('quick-add-custom-layout__media');

    if (media) mediaWrapper.appendChild(media);

    const detailsWrapper = document.createElement('div');
    detailsWrapper.classList.add('product-details', 'quick-add-custom-layout__details');

    const detailsGroup = document.createElement('div');
    detailsGroup.classList.add('group-block');

    const detailsContent = document.createElement('div');
    detailsContent.classList.add('group-block-content');

    if (variantPicker) detailsContent.appendChild(variantPicker);

    const variantNote = document.createElement('div');
    variantNote.classList.add('quick-add-variant-note');
    variantNote.setAttribute('data-quick-add-variant-note', '');
    variantNote.hidden = true;
    variantNote.innerHTML = `
      <div class="quick-add-variant-note__text" data-quick-add-variant-note-text></div>
    `;

    detailsContent.appendChild(variantNote);

    const specialOrder = document.createElement('div');
    specialOrder.classList.add('special-order-container');
    specialOrder.hidden = true;
    specialOrder.innerHTML = `
      <div class="special-order-container__inner"></div>
    `;
    detailsContent.appendChild(specialOrder);

    const purchaseBlock = buyButtonsBlock || productFormComponent;

    if (buyButtonsBlock) {
      detailsContent.appendChild(buyButtonsBlock);
    } else if (productFormComponent) {
      detailsContent.appendChild(productFormComponent);
    }

    const footerLinks = document.createElement('div');
    footerLinks.classList.add('quick-add-custom-layout__footer-links');

    // Custom quick-drawer wishlist removed.
    // Notify Me! will handle wishlist functionality outside this custom button.

    if (viewMoreDetails) {
      footerLinks.appendChild(viewMoreDetails.cloneNode(true));
    } else {
      const viewProductWrapper = document.createElement('div');
      viewProductWrapper.classList.add('view-more-details__wrapper');

      const viewProductLink = document.createElement('a');
      viewProductLink.classList.add('view-more-details');
      viewProductLink.href = this.productPageUrl;
      viewProductLink.textContent = window.themeStrings?.viewProduct || 'View product';

      viewProductWrapper.appendChild(viewProductLink);
      footerLinks.appendChild(viewProductWrapper);
    }

    detailsContent.appendChild(footerLinks);
    detailsGroup.appendChild(detailsContent);
    detailsWrapper.appendChild(detailsGroup);

    wrapper.appendChild(headerWrapper);
    if (media) wrapper.appendChild(mediaWrapper);
    wrapper.appendChild(detailsWrapper);

    return wrapper;
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // Remove conflicting ref="dialog" from any nested zoom/gallery dialogs
    // to prevent Horizon's ref resolver from overwriting quick-add-dialog's own ref
    productGrid.querySelectorAll('dialog[ref="dialog"]').forEach(el => el.removeAttribute('ref'));

    const cleanQuickAddContent = this.#buildQuickAddContent(productGrid);
    morph(modalContent, cleanQuickAddContent);
    modalContent.removeAttribute('aria-busy');

    this.#syncVariantSelection(modalContent);
    this.#bindVariantNoteUpdates(modalContent);
    this.#updateVariantNote(modalContent);
    this.#updateSpecialOrderState(modalContent);
    this.#applyPreorderButtonState(modalContent);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateSpecialOrderState(modalContent);
        this.#applyPreorderButtonState(modalContent);
      });
    });
  }

  #normalizeAvailabilityMode(value) {
    return String(value || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  #getCheckedVariantInput(modalContent) {
    return modalContent.querySelector('input[type="radio"][data-variant-id]:checked');
  }

  #getSelectedVariantAvailabilityData(modalContent) {
    const checkedInput = this.#getCheckedVariantInput(modalContent);
    const variantId = checkedInput?.dataset.variantId || '';

    if (!variantId) return null;

    const dataScripts = modalContent.querySelectorAll(
      'script[id^="availability-variant-data-test-"]'
    );

    for (const dataScript of dataScripts) {
      try {
        const variants = JSON.parse(dataScript.textContent || '{}');
        const variantData = variants[String(variantId)];

        if (variantData) return variantData;
      } catch (error) {
        console.warn(
          '[Quick Add] Could not parse variant availability data:',
          error
        );
      }
    }

    return null;
  }

  #getVariantMode(modalContent) {
    const checkedInput = this.#getCheckedVariantInput(modalContent);
    if (!checkedInput) return 'select_size';

    const checkedLabel = checkedInput.closest('.variant-option__button-label');
    const variantData = this.#getSelectedVariantAvailabilityData(modalContent) || {};

    const rawAvailabilityMode =
      checkedInput.dataset.availabilityMode ||
      checkedLabel?.dataset.availabilityMode ||
      checkedInput.getAttribute('data-availability-mode') ||
      checkedLabel?.getAttribute('data-availability-mode') ||
      variantData.mode ||
      '';

    const availabilityMode = this.#normalizeAvailabilityMode(rawAvailabilityMode);

    if (
      availabilityMode === 'special_order' ||
      availabilityMode === 'specialorder' ||
      availabilityMode === 'special_order_now' ||
      availabilityMode === 'specialordernow' ||
      checkedLabel?.classList.contains('is-special-order-mode')
    ) {
      return 'special_order';
    }

    if (
      availabilityMode === 'preorder' ||
      availabilityMode === 'pre_order' ||
      availabilityMode === 'pre_order_now' ||
      availabilityMode === 'preordernow' ||
      checkedLabel?.classList.contains('is-preorder-mode')
    ) {
      return 'preorder';
    }

    if (
      availabilityMode === 'notify_me' ||
      availabilityMode === 'notifyme' ||
      availabilityMode === 'notify' ||
      availabilityMode === 'back_in_stock' ||
      availabilityMode === 'backinstock' ||
      checkedLabel?.classList.contains('is-notify-mode')
    ) {
      return 'notify';
    }

    /*
     * Only use selling-plan IDs attached to the selected variant option.
     * Do not use the form's hidden selling_plan input to detect the mode,
     * because this class also writes that input and it can become stale.
     */
    const selectedSellingPlanId = String(
      checkedInput.dataset.sellingPlanId ||
      checkedLabel?.dataset.sellingPlanId ||
      checkedInput.getAttribute('data-selling-plan-id') ||
      checkedLabel?.getAttribute('data-selling-plan-id') ||
      ''
    ).trim();

    if (
      selectedSellingPlanId &&
      SELLING_PLAN_CONFIG[selectedSellingPlanId]
    ) {
      return SELLING_PLAN_CONFIG[selectedSellingPlanId].mode;
    }

    const inventoryQuantity = Number.parseInt(
      variantData.inventory_quantity ??
        checkedInput.dataset.inventoryQuantity ??
        '0',
      10
    );

    const inventoryPolicy = String(
      variantData.inventory_policy ||
      checkedInput.dataset.inventoryPolicy ||
      checkedLabel?.dataset.inventoryPolicy ||
      ''
    ).trim();

    const inventoryManagement = String(
      variantData.inventory_management ||
      checkedInput.dataset.inventoryManagement ||
      checkedLabel?.dataset.inventoryManagement ||
      ''
    ).trim();

    const variantAvailable =
      typeof variantData.available === 'boolean'
        ? variantData.available
        : !checkedInput.disabled;

    const isManagedSoldOut =
      variantAvailable === false &&
      inventoryManagement === 'shopify' &&
      inventoryPolicy === 'deny' &&
      Number.isFinite(inventoryQuantity) &&
      inventoryQuantity <= 0;

    if (
      checkedLabel?.classList.contains('unavailable') ||
      checkedLabel?.classList.contains('sold-out') ||
      checkedInput.disabled ||
      isManagedSoldOut
    ) {
      return 'notify';
    }

    return 'normal';
  }

  #getProductForm(modalContent) {
    return (
      modalContent.querySelector('product-form-component form[action*="/cart/add"]') ||
      modalContent.querySelector('form[action*="/cart/add"]')
    );
  }

  #setFormSellingPlan(modalContent, sellingPlanId = '') {
    const form = this.#getProductForm(modalContent);
    if (!form) return;

    const existingControls = Array.from(
      form.querySelectorAll(
        'input[name="selling_plan"], select[name="selling_plan"]'
      )
    );

    if (!sellingPlanId) {
      existingControls.forEach((control) => control.remove());
      return;
    }

    let sellingPlanInput = existingControls.find((control) => {
      return (
        control instanceof HTMLInputElement &&
        control.type === 'hidden'
      );
    });

    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }

    sellingPlanInput.value = String(sellingPlanId);
    sellingPlanInput.setAttribute('value', String(sellingPlanId));

    existingControls.forEach((control) => {
      if (control !== sellingPlanInput) {
        control.remove();
      }
    });
  }

  #setAddToCartButtonText(addToCartBtn, text) {
    if (!addToCartBtn || !text) return;

    const textTarget =
      addToCartBtn.querySelector('.add-to-cart-text__content span span') ||
      addToCartBtn.querySelector('.add-to-cart-text__content span') ||
      addToCartBtn.querySelector('.add-to-cart-text__content') ||
      addToCartBtn.querySelector('.add-to-cart-text') ||
      addToCartBtn.querySelector('span');

    if (textTarget) {
      textTarget.textContent = text;
    } else {
      addToCartBtn.textContent = text;
    }
  }

  #openNotifyMe(modalContent) {
    const notifyPortal = modalContent.querySelector('.nm-portal') || document.querySelector('.nm-portal');

    const notifyTrigger =
      modalContent.querySelector('[data-nm-open]') ||
      modalContent.querySelector('[data-notify-me-trigger]') ||
      modalContent.querySelector('.notify-me-button') ||
      modalContent.querySelector('.nm-notify-button') ||
      modalContent.querySelector('.nm-open-button') ||
      notifyPortal?.querySelector('button') ||
      notifyPortal?.querySelector('[role="button"]');

    if (notifyPortal) {
      notifyPortal.classList.add('quick-add-nm-portal');
      notifyPortal.hidden = false;
      notifyPortal.removeAttribute('aria-hidden');
    }

    if (notifyTrigger && typeof notifyTrigger.click === 'function') {
      notifyTrigger.click();
      return true;
    }

    document.dispatchEvent(
      new CustomEvent('quick-add:notify-me', {
        bubbles: true,
        detail: {
          modalContent,
          variantId: this.#getCheckedVariantInput(modalContent)?.dataset.variantId || '',
        },
      })
    );

    return Boolean(notifyPortal);
  }

  #applyPreorderButtonState(modalContent) {
    const mode = this.#getVariantMode(modalContent);
    const config = MODE_CONFIG[mode] || MODE_CONFIG.normal;

    const addToCartComponent = modalContent.querySelector('add-to-cart-component');
    const addToCartBtn = modalContent.querySelector('.add-to-cart-button');
    const acceleratedCheckout = modalContent.querySelector('.accelerated-checkout-block');
    const notifyPortal = modalContent.querySelector('.nm-portal');

    if (!addToCartComponent || !addToCartBtn) return;

    ['_preorderHandler', '_notifyHandler'].forEach((key) => {
      if (addToCartBtn[key]) {
        addToCartBtn.removeEventListener('click', addToCartBtn[key]);
        delete addToCartBtn[key];
      }
    });

    addToCartComponent.hidden = false;
    addToCartBtn.disabled = false;
    addToCartBtn.removeAttribute('disabled');
    addToCartBtn.classList.remove(
      'hengam-preorder-button',
      'is-special-order',
      'is-preorder',
      'is-notify-me'
    );

    modalContent.classList.remove(
      'quick-add-mode-special-order',
      'quick-add-mode-preorder',
      'quick-add-mode-notify',
      'quick-add-mode-normal'
    );

    modalContent.classList.add(`quick-add-mode-${mode.replace('_', '-')}`);
    modalContent.setAttribute('data-availability-mode', mode);


    if (notifyPortal) {
      notifyPortal.classList.add('quick-add-nm-portal');
      notifyPortal.hidden = true;
      notifyPortal.setAttribute('aria-hidden', 'true');
    }

    if (mode === 'special_order') {
      addToCartBtn.classList.add('hengam-preorder-button', 'is-special-order');
      this.#setAddToCartButtonText(addToCartBtn, config.buttonText);
      this.#setFormSellingPlan(modalContent, config.sellingPlanId);

      if (acceleratedCheckout) acceleratedCheckout.hidden = true;

      return;
    }

    if (mode === 'preorder') {
      addToCartBtn.classList.add('hengam-preorder-button', 'is-preorder');
      this.#setAddToCartButtonText(addToCartBtn, config.buttonText);
      this.#setFormSellingPlan(modalContent, config.sellingPlanId);

      if (acceleratedCheckout) acceleratedCheckout.hidden = true;

      return;
    }

    if (mode === 'notify') {
      addToCartBtn.classList.add('is-notify-me');
      this.#setAddToCartButtonText(addToCartBtn, config.buttonText);
      this.#setFormSellingPlan(modalContent, '');

      if (acceleratedCheckout) acceleratedCheckout.hidden = true;

      const notifyHandler = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        this.#openNotifyMe(modalContent);
      };

      addToCartBtn._notifyHandler = notifyHandler;
      addToCartBtn.addEventListener('click', notifyHandler);

      return;
    }

    this.#setAddToCartButtonText(addToCartBtn, config.buttonText);
    this.#setFormSellingPlan(modalContent, '');

    if (mode === 'select_size') {
      addToCartBtn.disabled = true;
      addToCartBtn.setAttribute('disabled', 'disabled');

      if (acceleratedCheckout) acceleratedCheckout.hidden = true;

      return;
    }

    if (acceleratedCheckout) acceleratedCheckout.hidden = false;
  }

  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  #getVariantNotes(modalContent) {
    let json = this.getAttribute('data-variant-size-notes-json') || '';

    if (!json || json === '{}') {
      const wrapper = modalContent.querySelector('.quick-add-custom-layout');
      json = wrapper?.getAttribute('data-variant-size-notes-json') || '';
    }

    if (!json || json === '{}') {
      const script = modalContent.querySelector('[data-variant-size-notes]');
      json = script?.textContent?.trim() || '{}';
    }

    try {
      return JSON.parse(json);
    } catch (error) {
      console.warn('Variant size note JSON parse error:', error, json);
      return {};
    }
  }

  #hideVariantNote(modalContent) {
    const noteBox = modalContent.querySelector('[data-quick-add-variant-note]');
    const noteText = modalContent.querySelector('[data-quick-add-variant-note-text]');

    if (!noteBox || !noteText) return;

    noteBox.hidden = true;
    noteText.innerHTML = '';
    noteBox.removeAttribute('data-active-variant-id');

    modalContent.querySelectorAll('.variant-note-icon.is-active').forEach((icon) => {
      icon.classList.remove('is-active');
      icon.setAttribute('aria-label', 'Show variant note');
    });
  }

  #updateVariantNote(modalContent) {
    const noteBox = modalContent.querySelector('[data-quick-add-variant-note]');
    const noteText = modalContent.querySelector('[data-quick-add-variant-note-text]');

    if (!noteBox || !noteText) return;

    const notes = this.#getVariantNotes(modalContent);
    this.#markVariantOptionsWithNotes(modalContent, notes);

    const allIcons = modalContent.querySelectorAll('.variant-note-icon');

    if (!allIcons.length) {
      this.#hideVariantNote(modalContent);
      return;
    }

    const activeVariantId = noteBox.getAttribute('data-active-variant-id') || '';
    const activeIcon = activeVariantId
      ? modalContent.querySelector(`.variant-note-icon[data-variant-id="${activeVariantId}"]`)
      : null;

    if (
      !activeVariantId ||
      !notes[activeVariantId] ||
      String(notes[activeVariantId]).trim() === '' ||
      !activeIcon
    ) {
      this.#hideVariantNote(modalContent);
    }
  }

  #bindVariantNoteUpdates(modalContent) {
    this.#variantNoteObserver?.disconnect();
    this.#variantNoteAbortController?.abort();

    this.#variantNoteAbortController = new AbortController();

    let refreshFrame = null;

    const refresh = () => {
      if (refreshFrame) return;

      refreshFrame = window.requestAnimationFrame(() => {
        refreshFrame = null;

        this.#updateVariantNote(modalContent);

        window.requestAnimationFrame(() => {
          this.#updateSpecialOrderState(modalContent);
          this.#applyPreorderButtonState(modalContent);
        });
      });
    };

    modalContent.addEventListener('change', refresh, {
      signal: this.#variantNoteAbortController.signal,
    });

    document.addEventListener(ThemeEvents.variantUpdate, refresh, {
      signal: this.#variantNoteAbortController.signal,
    });

    const variantPicker = modalContent.querySelector('variant-picker');

    if (variantPicker) {
      this.#variantNoteObserver = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => {
          if (mutation.type === 'attributes') return true;

          if (mutation.type === 'childList') {
            const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

            if (!changedNodes.length) return false;

            const onlyNoteIconChanged = changedNodes.every((node) => {
              return (
                node.nodeType === Node.ELEMENT_NODE &&
                node instanceof HTMLElement &&
                node.classList.contains('variant-note-icon')
              );
            });

            return !onlyNoteIconChanged;
          }

          return false;
        });

        if (shouldRefresh) refresh();
      });

      this.#variantNoteObserver.observe(variantPicker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'data-current-checked', 'aria-checked'],
      });
    }

    this.#updateVariantNote(modalContent);
  }

  #markVariantOptionsWithNotes(modalContent, notes) {
    const inputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    const noteBox = modalContent.querySelector('[data-quick-add-variant-note]');
    const noteText = modalContent.querySelector('[data-quick-add-variant-note-text]');

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = input.closest('.variant-option__button-label');
      if (!label) return;

      const fieldset = label.closest('fieldset.variant-option');
      const legendText = fieldset?.querySelector('legend')?.textContent?.trim().toLowerCase() || '';

      const isSizeOption = ['size', 'taglia', 'tamaño', 'taille', 'größe'].includes(legendText);

      if (!isSizeOption) {
        label.classList.remove('has-variant-note');
        label.querySelector('.variant-note-icon')?.remove();
        return;
      }

      const variantId = input.dataset.variantId || '';
      const note = variantId ? notes[variantId] : '';
      const hasNote = note && String(note).trim() !== '';

      label.classList.toggle('has-variant-note', Boolean(hasNote));

      let icon = label.querySelector('.variant-note-icon');

      if (!hasNote) {
        icon?.remove();
        return;
      }

      if (!icon) {
        icon = document.createElement('button');
        icon.type = 'button';
        icon.classList.add('variant-note-icon');
        icon.setAttribute('aria-label', 'Show variant note');
        icon.setAttribute('data-variant-note-icon', '');

        icon.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1ZM8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM6.5 11C6.225 11 6 11.225 6 11.5C6 11.775 6.225 12 6.5 12H9.5C9.775 12 10 11.775 10 11.5C10 11.225 9.775 11 9.5 11H8.5V7.5C8.5 7.225 8.275 7 8 7H6.5C6.225 7 6 7.225 6 7.5C6 7.775 6.225 8 6.5 8H7.5V11H6.5ZM8 5.75C8.19891 5.75 8.38968 5.67098 8.53033 5.53033C8.67098 5.38968 8.75 5.19891 8.75 5C8.75 4.80109 8.67098 4.61032 8.53033 4.46967C8.38968 4.32902 8.19891 4.25 8 4.25C7.80109 4.25 7.61032 4.32902 7.46967 4.46967C7.32902 4.61032 7.25 5.19891 7.25 5C7.25 5.19891 7.32902 5.38968 7.46967 5.53033C7.61032 5.67098 7.80109 5.75 8 5.75Z" fill="#626F82"/>
          </svg>
        `;

        icon.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!noteBox || !noteText) return;

          const clickedVariantId = icon.getAttribute('data-variant-id') || '';
          const clickedNote = icon.getAttribute('data-variant-note') || '';
          const currentActiveVariantId = noteBox.getAttribute('data-active-variant-id') || '';

          modalContent.querySelectorAll('.variant-note-icon.is-active').forEach((activeIcon) => {
            activeIcon.classList.remove('is-active');
            activeIcon.setAttribute('aria-label', 'Show variant note');
          });

          if (!noteBox.hidden && currentActiveVariantId === clickedVariantId) {
            this.#hideVariantNote(modalContent);
            return;
          }

          noteBox.hidden = false;
          noteBox.setAttribute('data-active-variant-id', clickedVariantId);
          noteText.innerHTML = clickedNote.replace(/\n/g, '<br>');
          icon.classList.add('is-active');
          icon.setAttribute('aria-label', 'Hide variant note');
        });

        const text = label.querySelector('.variant-option__button-label__text');

        if (text) {
          text.insertAdjacentElement('afterend', icon);
        } else {
          label.appendChild(icon);
        }
      }

      icon.setAttribute('data-variant-id', variantId);
      icon.setAttribute('data-variant-note', String(note));
    });
  }

  #updateSpecialOrderState(modalContent) {
    const mode = this.#getVariantMode(modalContent);
    const specialOrder = modalContent.querySelector('.special-order-container');
    const specialOrderInner = modalContent.querySelector('.special-order-container__inner');

    if (!specialOrder || !specialOrderInner) return;

    const config = MODE_CONFIG[mode] || MODE_CONFIG.normal;
    const activeMessage = config.message || '';

    if (activeMessage) {
      specialOrderInner.innerHTML = `
        <div class="special-order-container__description">
          ${activeMessage}
        </div>
      `;

      specialOrder.hidden = false;
      specialOrder.setAttribute('data-availability-mode', mode);
      return;
    }

    specialOrder.hidden = true;
    specialOrderInner.innerHTML = '';
    specialOrder.removeAttribute('data-availability-mode');
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');
    const customViewProductLink = this.querySelector('.view-more-details');

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (customViewProductLink) customViewProductLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');

      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/* ==================================================
   QUICK ADD VIEWPORT TOOLTIP
================================================== */

(() => {
  'use strict';

  /*
   * Remove the flag left by an older copy of this script when the
   * theme editor reloads assets without performing a full page reload.
   */
  if (window.__quickAddDynamicTooltipCleanup) {
    window.__quickAddDynamicTooltipCleanup();
  }

  const ICON_SELECTOR =
    '#quick-add-modal-content .special-order-icon';

  const SOURCE_TOOLTIP_SELECTOR =
    '.special-order-tooltip';

  const PORTAL_CLASS =
    'quick-add-dynamic-tooltip-portal';

  const STYLE_ID =
    'quick-add-dynamic-tooltip-styles';

  const VIEWPORT_GAP = 10;
  const ICON_GAP = 10;
  const PREFERRED_WIDTH = 220;
  const MINIMUM_WIDTH = 120;
  const ARROW_EDGE_GAP = 16;

  let activeIcon = null;
  let activePortal = null;
  let tooltipPinned = false;
  let animationFrame = null;

  const eventController = new AbortController();
  const eventSignal = eventController.signal;

  const clamp = (value, minimum, maximum) => {
    return Math.min(Math.max(value, minimum), maximum);
  };

  const injectStyles = () => {
    document.getElementById(STYLE_ID)?.remove();

    const style = document.createElement('style');
    style.id = STYLE_ID;

    style.textContent = `
      /*
       * Hide the original tooltip contained inside each size label.
       * The script creates one dynamically positioned copy.
       */
      #quick-add-modal-content .special-order-tooltip {
        display: none !important;
      }

      .${PORTAL_CLASS} {
        --quick-tooltip-arrow-left: 50%;

        /*
         * IMPORTANT:
         * The portal lives inside the native dialog and uses absolute
         * coordinates relative to its actual containing block. Using
         * position: fixed here causes incorrect desktop positioning
         * when the drawer is animated with transform.
         */
        position: absolute !important;
        z-index: 2147483647 !important;

        display: block !important;
        box-sizing: border-box !important;

        width: 220px;
        max-width: calc(100% - 20px);

        margin: 0 !important;
        padding: 10px 12px !important;

        color: #626f82 !important;
        background: #ffffff !important;

        border: 1px solid rgba(0, 0, 0, 0.12) !important;
        border-radius: 6px !important;

        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;

        font-family: inherit !important;
        font-size: 12px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 1.4 !important;
        text-align: left !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;

        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: none !important;
      }

      .${PORTAL_CLASS}[hidden] {
        display: none !important;
      }

      .${PORTAL_CLASS} > :first-child {
        margin-top: 0 !important;
      }

      .${PORTAL_CLASS} > :last-child {
        margin-bottom: 0 !important;
      }

      .${PORTAL_CLASS} p {
        margin: 0 !important;
        color: inherit !important;
        font: inherit !important;
      }

      .${PORTAL_CLASS}::after {
        content: "" !important;
        position: absolute !important;

        left: var(--quick-tooltip-arrow-left) !important;
        transform: translateX(-50%) !important;

        width: 0 !important;
        height: 0 !important;

        margin: 0 !important;
        padding: 0 !important;

        background: transparent !important;
        box-shadow: none !important;
        pointer-events: none !important;
      }

      .${PORTAL_CLASS}:not(.is-below)::after {
        top: calc(100% - 1px) !important;
        bottom: auto !important;

        border-left: 6px solid transparent !important;
        border-right: 6px solid transparent !important;
        border-top: 7px solid #ffffff !important;
        border-bottom: 0 !important;
      }

      .${PORTAL_CLASS}.is-below::after {
        top: auto !important;
        bottom: calc(100% - 1px) !important;

        border-left: 6px solid transparent !important;
        border-right: 6px solid transparent !important;
        border-bottom: 7px solid #ffffff !important;
        border-top: 0 !important;
      }
    `;

    document.head.appendChild(style);
  };

  const getQuickAddDialog = (icon) => {
    if (!(icon instanceof HTMLElement)) return null;

    return (
      icon.closest('dialog') ||
      document.querySelector('#quick-add-dialog dialog')
    );
  };

  const getPortal = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return null;

    let portal = dialog.querySelector(
      `:scope > .${PORTAL_CLASS}`
    );

    if (!portal) {
      portal = document.createElement('div');
      portal.className = PORTAL_CLASS;
      portal.setAttribute('role', 'tooltip');
      portal.hidden = true;

      /*
       * The portal must remain inside the open native dialog so that it
       * shares the browser top layer and is not hidden behind the drawer.
       */
      dialog.appendChild(portal);
    }

    return portal;
  };

  const getVisibleBounds = (dialog) => {
    const dialogRect = dialog.getBoundingClientRect();

    return {
      left: Math.max(
        VIEWPORT_GAP,
        dialogRect.left + VIEWPORT_GAP
      ),

      right: Math.min(
        window.innerWidth - VIEWPORT_GAP,
        dialogRect.right - VIEWPORT_GAP
      ),

      top: Math.max(
        VIEWPORT_GAP,
        dialogRect.top + VIEWPORT_GAP
      ),

      bottom: Math.min(
        window.innerHeight - VIEWPORT_GAP,
        dialogRect.bottom - VIEWPORT_GAP
      ),
    };
  };

  /*
   * Convert viewport coordinates into coordinates local to the portal's
   * containing block. This is the desktop fix: the Quick Add drawer uses
   * transforms, so assigning viewport coordinates directly to an element
   * inside the dialog produces a large unwanted offset.
   */
  const viewportToLocalCoordinates = (
    portal,
    viewportLeft,
    viewportTop
  ) => {
    const containingBlock =
      portal.offsetParent instanceof HTMLElement
        ? portal.offsetParent
        : portal.parentElement;

    if (!(containingBlock instanceof HTMLElement)) {
      return {
        left: viewportLeft,
        top: viewportTop,
      };
    }

    const containingRect =
      containingBlock.getBoundingClientRect();

    return {
      left:
        viewportLeft -
        containingRect.left -
        containingBlock.clientLeft +
        containingBlock.scrollLeft,

      top:
        viewportTop -
        containingRect.top -
        containingBlock.clientTop +
        containingBlock.scrollTop,
    };
  };

  const clearPortalStyles = (portal) => {
    portal.classList.remove('is-below');

    portal.style.removeProperty('left');
    portal.style.removeProperty('top');
    portal.style.removeProperty('width');
    portal.style.removeProperty('visibility');
    portal.style.removeProperty('--quick-tooltip-arrow-left');
  };

  const hideTooltip = () => {
    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }

    if (activePortal) {
      activePortal.hidden = true;
      activePortal.innerHTML = '';
      clearPortalStyles(activePortal);
    }

    activeIcon = null;
    activePortal = null;
    tooltipPinned = false;
  };

  const positionTooltip = (icon) => {
    if (
      !(icon instanceof HTMLElement) ||
      !icon.isConnected
    ) {
      hideTooltip();
      return;
    }

    const sourceTooltip = icon.querySelector(
      SOURCE_TOOLTIP_SELECTOR
    );

    if (!(sourceTooltip instanceof HTMLElement)) {
      hideTooltip();
      return;
    }

    const dialog = getQuickAddDialog(icon);

    if (
      !(dialog instanceof HTMLDialogElement) ||
      !dialog.open
    ) {
      hideTooltip();
      return;
    }

    const portal = getPortal(dialog);

    if (!(portal instanceof HTMLElement)) {
      hideTooltip();
      return;
    }

    activeIcon = icon;
    activePortal = portal;

    portal.innerHTML =
      sourceTooltip.innerHTML.trim() ||
      sourceTooltip.textContent.trim();

    portal.hidden = false;
    portal.classList.remove('is-below');

    const bounds = getVisibleBounds(dialog);
    const boundsWidth = Math.max(
      0,
      bounds.right - bounds.left
    );

    if (boundsWidth < MINIMUM_WIDTH) {
      hideTooltip();
      return;
    }

    const tooltipWidth = Math.min(
      PREFERRED_WIDTH,
      boundsWidth
    );

    portal.style.width =
      `${Math.floor(tooltipWidth)}px`;

    /*
     * Place it temporarily in a known local position so its height can
     * be measured without allowing an old coordinate to affect layout.
     */
    portal.style.left = '0px';
    portal.style.top = '0px';
    portal.style.visibility = 'hidden';

    const iconRect = icon.getBoundingClientRect();
    const tooltipHeight =
      portal.getBoundingClientRect().height;

    const iconCenterX =
      iconRect.left + iconRect.width / 2;

    const idealViewportLeft =
      iconCenterX - tooltipWidth / 2;

    const maximumViewportLeft = Math.max(
      bounds.left,
      bounds.right - tooltipWidth
    );

    const tooltipViewportLeft = clamp(
      idealViewportLeft,
      bounds.left,
      maximumViewportLeft
    );

    const availableAbove =
      iconRect.top - bounds.top;

    const availableBelow =
      bounds.bottom - iconRect.bottom;

    const fitsAbove =
      availableAbove >= tooltipHeight + ICON_GAP;

    const fitsBelow =
      availableBelow >= tooltipHeight + ICON_GAP;

    let placeBelow = false;

    if (!fitsAbove && fitsBelow) {
      placeBelow = true;
    } else if (!fitsAbove && !fitsBelow) {
      placeBelow =
        availableBelow > availableAbove;
    }

    let tooltipViewportTop = placeBelow
      ? iconRect.bottom + ICON_GAP
      : iconRect.top -
        tooltipHeight -
        ICON_GAP;

    const maximumViewportTop = Math.max(
      bounds.top,
      bounds.bottom - tooltipHeight
    );

    tooltipViewportTop = clamp(
      tooltipViewportTop,
      bounds.top,
      maximumViewportTop
    );

    const arrowMaximum = Math.max(
      ARROW_EDGE_GAP,
      tooltipWidth - ARROW_EDGE_GAP
    );

    const arrowLeft = clamp(
      iconCenterX - tooltipViewportLeft,
      ARROW_EDGE_GAP,
      arrowMaximum
    );

    const localPosition =
      viewportToLocalCoordinates(
        portal,
        tooltipViewportLeft,
        tooltipViewportTop
      );

    portal.classList.toggle(
      'is-below',
      placeBelow
    );

    portal.style.left =
      `${Math.round(localPosition.left)}px`;

    portal.style.top =
      `${Math.round(localPosition.top)}px`;

    portal.style.setProperty(
      '--quick-tooltip-arrow-left',
      `${Math.round(arrowLeft)}px`
    );

    portal.style.visibility = 'visible';
  };

  const schedulePosition = (icon) => {
    if (!(icon instanceof HTMLElement)) return;

    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
    }

    animationFrame =
      window.requestAnimationFrame(() => {
        animationFrame = null;
        positionTooltip(icon);
      });
  };

  const getEventIcon = (event) => {
    if (!(event.target instanceof Element)) {
      return null;
    }

    return event.target.closest(ICON_SELECTOR);
  };

  const handlePointerOver = (event) => {
    const icon = getEventIcon(event);

    if (!icon) return;

    /*
     * Ignore pointer movement between children of the same icon.
     */
    if (
      event.relatedTarget instanceof Node &&
      icon.contains(event.relatedTarget)
    ) {
      return;
    }

    schedulePosition(icon);
  };

  const handlePointerOut = (event) => {
    const icon = getEventIcon(event);

    if (!icon || tooltipPinned) return;

    if (
      event.relatedTarget instanceof Node &&
      icon.contains(event.relatedTarget)
    ) {
      return;
    }

    hideTooltip();
  };

  const handleFocusIn = (event) => {
    const icon = getEventIcon(event);

    if (icon) {
      schedulePosition(icon);
    }
  };

  const handleFocusOut = (event) => {
    const icon = getEventIcon(event);

    if (!icon || tooltipPinned) return;

    if (
      event.relatedTarget instanceof Node &&
      icon.contains(event.relatedTarget)
    ) {
      return;
    }

    hideTooltip();
  };

  const handleClick = (event) => {
    const icon = getEventIcon(event);

    if (icon) {
      event.preventDefault();
      event.stopPropagation();

      if (
        tooltipPinned &&
        activeIcon === icon &&
        activePortal &&
        !activePortal.hidden
      ) {
        hideTooltip();
        return;
      }

      tooltipPinned = true;
      schedulePosition(icon);
      return;
    }

    if (tooltipPinned) {
      hideTooltip();
    }
  };

  const handleResizeOrScroll = () => {
    if (activeIcon?.isConnected) {
      schedulePosition(activeIcon);
    }
  };

  const handleDialogClose = (event) => {
    if (
      event.target instanceof HTMLDialogElement &&
      event.target.closest('#quick-add-dialog')
    ) {
      hideTooltip();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      hideTooltip();
    }
  };

  document.addEventListener(
    'pointerover',
    handlePointerOver,
    { signal: eventSignal }
  );

  document.addEventListener(
    'pointerout',
    handlePointerOut,
    { signal: eventSignal }
  );

  document.addEventListener(
    'focusin',
    handleFocusIn,
    { signal: eventSignal }
  );

  document.addEventListener(
    'focusout',
    handleFocusOut,
    { signal: eventSignal }
  );

  document.addEventListener(
    'click',
    handleClick,
    { signal: eventSignal }
  );

  window.addEventListener(
    'resize',
    handleResizeOrScroll,
    {
      passive: true,
      signal: eventSignal,
    }
  );

  document.addEventListener(
    'scroll',
    handleResizeOrScroll,
    {
      capture: true,
      passive: true,
      signal: eventSignal,
    }
  );

  document.addEventListener(
    'close',
    handleDialogClose,
    {
      capture: true,
      signal: eventSignal,
    }
  );

  document.addEventListener(
    'keydown',
    handleKeyDown,
    { signal: eventSignal }
  );

  injectStyles();

  window.__quickAddDynamicTooltipCleanup = () => {
    eventController.abort();

    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
    }

    document
      .querySelectorAll(`.${PORTAL_CLASS}`)
      .forEach((portal) => portal.remove());

    document.getElementById(STYLE_ID)?.remove();

    delete window.__quickAddDynamicTooltipCleanup;
  };
})();